import { useCallback, useEffect, useMemo, useRef } from "react";
import { createFileRoute, Link, notFound, useNavigate, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LessonMarkdown } from "@/components/cockpit/LessonMarkdown.lazy";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  ListTree,
} from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { reportCourseCompletion } from "@/lib/course-completion.functions";
import { useCockpitSession } from "@/hooks/use-cockpit-session";
import {
  courseQueryOptions,
  flattenLessons,
  pickInitialLesson,
  progressQueryOptions,
  writeProgress,
  type ProgressRow,
} from "@/lib/cockpit-courses";
import { CourseSidebar } from "@/components/cockpit/CourseSidebar";
import { CourseLessonPlayer } from "@/components/cockpit/CourseLessonPlayer";
import { LessonMaterials } from "@/components/cockpit/LessonMaterials";
import {
  getCourseVideoConsent,
  grantCourseVideoConsent,
  revokeCourseVideoConsent,
} from "@/lib/course-video-consent.functions";
import type { CourseVideoConsentState } from "@/lib/course-video-consent";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";


const searchSchema = z.object({
  lesson: fallback(z.string(), "").optional(),
});

export const Route = createFileRoute("/_cockpit/cockpit/kurse/$slug")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Kurs – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitCoursePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl p-8">
      <h1 className="font-display text-3xl font-extrabold text-primary">
        Kurs nicht verfügbar
      </h1>
      <p className="mt-3 text-muted-foreground">
        Dieser Kurs ist für Sie nicht verfügbar. Bitte wenden Sie sich an Ihren
        Ansprechpartner.
      </p>
      <Link
        to="/cockpit/kurse"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-extrabold text-primary hover:brightness-95"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Zurück zu Kurse
      </Link>
    </div>
  ),
  errorComponent: CourseErrorBoundary,
});

function CourseErrorBoundary({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Substanziell loggen — Detail landet in Browser-Konsole und via RUM/Sentry
    // erreichbar; für Nutzer bleibt die Meldung freundlich.
    // eslint-disable-next-line no-console
    console.error("[cockpit-course] route error", {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack?.slice(0, 800) : undefined,
      url: typeof window !== "undefined" ? window.location.href : null,
    });
  }, [error]);

  return (
    <div className="mx-auto max-w-3xl p-8">
      <h1 className="font-display text-3xl font-extrabold text-primary">
        Der Kurs konnte nicht geladen werden
      </h1>
      <p className="mt-3 text-muted-foreground">
        Es ist ein technischer Fehler aufgetreten. Bitte versuchen Sie es
        erneut. Bleibt das Problem bestehen, wenden Sie sich an Ihren
        Ansprechpartner.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={async () => {
            await queryClient.invalidateQueries({ queryKey: ["cockpit", "course"] });
            await router.invalidate();
            reset();
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-extrabold text-primary hover:brightness-95"
        >
          Erneut versuchen
        </button>
        <Link
          to="/cockpit/kurse"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-secondary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Zurück zu Kurse
        </Link>
      </div>
    </div>
  );
}

function CockpitCoursePage() {
  const { slug } = Route.useParams();
  const { lesson: activeLessonParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useCockpitSession();
  const queryClient = useQueryClient();

  const courseQ = useQuery(courseQueryOptions(slug));
  const course = courseQ.data ?? null;
  const courseId = course?.id ?? "";
  const userId = session.userId ?? "";

  const progressQ = useQuery({
    ...progressQueryOptions(userId, courseId),
    enabled: !!userId && !!courseId,
  });
  const progress: ProgressRow[] = progressQ.data ?? [];

  // Kurs-Video-Zustimmung (Vimeo) — am Konto gespeichert, gilt kursübergreifend.
  const consentKey = ["cockpit", "video-consent", userId] as const;
  const consentQ = useQuery<CourseVideoConsentState>({
    queryKey: consentKey,
    queryFn: () => getCourseVideoConsent(),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
  const consentState: "loading" | "granted" | "required" = consentQ.data
    ? consentQ.data.granted
      ? "granted"
      : "required"
    : "loading";

  const grantConsentMutation = useMutation({
    mutationFn: () => grantCourseVideoConsent(),
    onSuccess: (data) => {
      queryClient.setQueryData(consentKey, data);
    },
    onError: () => toast.error("Zustimmung konnte nicht gespeichert werden"),
  });

  const revokeConsentMutation = useMutation({
    mutationFn: () => revokeCourseVideoConsent(),
    onSuccess: (data) => {
      queryClient.setQueryData(consentKey, data);
      toast.success("Video-Zustimmung widerrufen. Vor dem nächsten Video fragen wir erneut.");
    },
    onError: () => toast.error("Widerruf konnte nicht gespeichert werden"),
  });



  const flat = useMemo(() => (course ? flattenLessons(course) : []), [course]);
  const activeExists = useMemo(
    () => !!activeLessonParam && flat.some((f) => f.lesson.id === activeLessonParam),
    [activeLessonParam, flat],
  );

  // Redirect only when there is no valid active lesson. Never during a
  // progress refetch (otherwise a lesson click races an in-flight refetch
  // and the URL flips back).
  useEffect(() => {
    if (!course || progressQ.isFetching) return;
    if (activeLessonParam && activeExists) return;
    const initial = pickInitialLesson(course, progress);
    if (initial) {
      navigate({ to: ".", search: { lesson: initial }, replace: true });
    }
  }, [course, activeLessonParam, activeExists, progress, progressQ.isFetching, navigate]);

  const currentIndex = useMemo(
    () => flat.findIndex((f) => f.lesson.id === activeLessonParam),
    [flat, activeLessonParam],
  );
  const current = currentIndex >= 0 ? flat[currentIndex] : null;
  const prev = currentIndex > 0 ? flat[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < flat.length - 1 ? flat[currentIndex + 1] : null;

  const currentProgress = current
    ? progress.find((p) => p.lesson_id === current.lesson.id)
    : undefined;
  const isCompleted = !!currentProgress?.completed_at;

  const progressKey = ["cockpit", "course", "progress", courseId, userId] as const;

  // Optimistic-only position write. No refetches — the sidebar reflects
  // completion via completion-only invalidation below.
  const writePositionOptimistic = useCallback(
    (lessonId: string, seconds: number) => {
      if (!userId || !courseId) return;
      queryClient.setQueryData<ProgressRow[]>(progressKey, (prev) => {
        const now = new Date().toISOString();
        const nextArr = prev ? [...prev] : [];
        const idx = nextArr.findIndex((p) => p.lesson_id === lessonId);
        if (idx >= 0) {
          nextArr[idx] = {
            ...nextArr[idx],
            last_position_seconds: Math.floor(seconds),
            updated_at: now,
          };
        } else {
          nextArr.push({
            lesson_id: lessonId,
            course_id: courseId,
            completed_at: null,
            last_position_seconds: Math.floor(seconds),
            updated_at: now,
          });
        }
        return nextArr;
      });
      void writeProgress({
        userId,
        courseId,
        lessonId,
        seconds,
        completed: !!progress.find((p) => p.lesson_id === lessonId)?.completed_at,
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[cockpit-course] position save failed", err);
      });
    },
    [courseId, userId, queryClient, progressKey, progress],
  );

  const reportCompletion = useServerFn(reportCourseCompletion);

  const completionMutation = useMutation({
    mutationFn: async (payload: { lesson_id: string; completed: boolean }) => {
      if (!userId || !courseId) throw new Error("Nicht eingeloggt");
      await writeProgress({
        userId,
        courseId,
        lessonId: payload.lesson_id,
        seconds: currentProgress?.last_position_seconds ?? 0,
        completed: payload.completed,
      });
    },
    onSuccess: (_res, payload) => {
      queryClient.invalidateQueries({ queryKey: progressKey });
      queryClient.invalidateQueries({ queryKey: ["cockpit", "courses"] });
      // Abschluss melden – nur beim Abschließen, nie beim Zurücksetzen.
      if (payload.completed && courseId) {
        void reportCompletion({ data: { courseId } }).catch(() => {
          /* Benachrichtigung darf den Fortschritt nie blockieren */
        });
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen"),
  });

  // Throttled time-update handler. Player already fires every ~10 s, but we
  // guard here explicitly so parent re-renders can't skew the cadence.
  const lastWriteAtRef = useRef(0);
  const lastLessonIdRef = useRef<string | null>(null);
  const lastSecondsRef = useRef(0);

  useEffect(() => {
    // Flush pending position when the lesson changes.
    const prevLesson = lastLessonIdRef.current;
    if (prevLesson && prevLesson !== current?.lesson.id && lastSecondsRef.current > 0) {
      writePositionOptimistic(prevLesson, lastSecondsRef.current);
    }
    lastLessonIdRef.current = current?.lesson.id ?? null;
    lastSecondsRef.current = 0;
    lastWriteAtRef.current = 0;
  }, [current?.lesson.id, writePositionOptimistic]);

  const handleTimeUpdate = useCallback(
    (seconds: number) => {
      const lessonId = lastLessonIdRef.current;
      if (!lessonId) return;
      lastSecondsRef.current = seconds;
      const now = Date.now();
      if (now - lastWriteAtRef.current < 10_000) return;
      lastWriteAtRef.current = now;
      writePositionOptimistic(lessonId, seconds);
    },
    [writePositionOptimistic],
  );

  const handlePause = useCallback(
    (seconds: number) => {
      const lessonId = lastLessonIdRef.current;
      if (!lessonId) return;
      lastSecondsRef.current = seconds;
      writePositionOptimistic(lessonId, seconds);
    },
    [writePositionOptimistic],
  );

  const handleEnded = useCallback(() => {
    const lessonId = lastLessonIdRef.current;
    if (!lessonId) return;
    // Completion via mutation (invalidates once) — not a hot-path write.
    completionMutation.mutate({ lesson_id: lessonId, completed: true });
    toast.success("Lektion abgeschlossen");
  }, [completionMutation]);

  // Save on page hide / tab close.
  useEffect(() => {
    const flush = () => {
      const lessonId = lastLessonIdRef.current;
      if (lessonId && lastSecondsRef.current > 0) {
        writePositionOptimistic(lessonId, lastSecondsRef.current);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [writePositionOptimistic]);

  function toggleCompleted() {
    if (!current) return;
    completionMutation.mutate(
      { lesson_id: current.lesson.id, completed: !isCompleted },
      {
        onSuccess: () =>
          toast.success(isCompleted ? "Lektion wieder geöffnet" : "Lektion abgeschlossen"),
      },
    );
  }

  // Handle not-found / RLS-hidden
  if (courseQ.isFetched && !courseQ.isFetching && !course) throw notFound();

  if (!course || session.loading) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <div className="hidden h-80 animate-pulse rounded-lg bg-secondary lg:block" />
          <div>
            <div className="aspect-video w-full animate-pulse rounded-md bg-secondary" />
            <div className="mt-4 h-6 w-2/3 animate-pulse rounded bg-secondary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Link
          to="/cockpit/kurse"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Zurück zu Kurse
        </Link>

        {/* Ticket W15: Kompaktkurse haben nur ein Video – keine Kursstruktur. */}
        <div className={course.courseFormat === "kompaktkurs" ? "hidden" : "lg:hidden"}>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <ListTree className="h-4 w-4" aria-hidden />
                Kursstruktur
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto p-0">
              <div className="p-4">
                <CourseSidebar
                  course={course}
                  progress={progress}
                  activeLessonId={activeLessonParam ?? null}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div
        className={
          course.courseFormat === "kompaktkurs"
            ? "grid gap-6"
            : "grid gap-6 lg:grid-cols-[18rem_1fr]"
        }
      >
        <div className={course.courseFormat === "kompaktkurs" ? "hidden" : "hidden lg:block"}>
          <div className="sticky top-20">
            <CourseSidebar
              course={course}
              progress={progress}
              activeLessonId={activeLessonParam ?? null}
            />
          </div>
        </div>

        <div className="min-w-0">
          {current ? (
            <>
              <h1 className="mb-4 font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
                {current.lesson.title}
              </h1>

              <CourseLessonPlayer
                vimeoUrl={current.lesson.vimeo_url}
                lessonKey={current.lesson.id}
                startAtSeconds={currentProgress?.last_position_seconds ?? 0}
                consentState={consentState}
                consentPending={grantConsentMutation.isPending}
                onGrantConsent={() => grantConsentMutation.mutate()}
                onTimeUpdate={handleTimeUpdate}
                onPause={handlePause}
                onEnded={handleEnded}
              />


              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={toggleCompleted}
                  disabled={completionMutation.isPending}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-extrabold transition-colors ${
                    isCompleted
                      ? "border border-border bg-card text-foreground hover:bg-secondary"
                      : "bg-brand-button text-primary hover:brightness-95"
                  }`}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                      Abgeschlossen · wieder öffnen
                    </>
                  ) : (
                    <>
                      <Circle className="h-4 w-4" aria-hidden />
                      Als abgeschlossen markieren
                    </>
                  )}
                </button>

                <div className="flex items-center gap-2">
                  <Link
                    to="/cockpit/kurse/$slug"
                    params={{ slug: course.slug }}
                    search={prev ? { lesson: prev.lesson.id } : undefined}
                    disabled={!prev}
                    className={`inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm ${
                      prev ? "hover:bg-secondary" : "pointer-events-none opacity-40"
                    }`}
                    aria-disabled={!prev}
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Zurück
                  </Link>
                  <Link
                    to="/cockpit/kurse/$slug"
                    params={{ slug: course.slug }}
                    search={next ? { lesson: next.lesson.id } : undefined}
                    disabled={!next}
                    className={`inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-extrabold text-primary-foreground ${
                      next ? "hover:brightness-105" : "pointer-events-none opacity-40"
                    }`}
                    aria-disabled={!next}
                  >
                    Weiter
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </div>

              {current.lesson.body_md?.trim() ? (
                <section className="prose prose-slate mt-8 max-w-none font-body">
                  <LessonMarkdown>{current.lesson.body_md}</LessonMarkdown>
                </section>
              ) : null}

              <section className="mt-10">
                <h2 className="font-display text-xl font-extrabold text-primary">
                  Materialien
                </h2>
                <div className="mt-3">
                  <LessonMaterials lessonId={current.lesson.id} />
                </div>
              </section>
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-muted-foreground">
                Dieser Kurs enthält aktuell keine Lektionen.
              </p>
            </div>
          )}

          {consentState === "granted" ? (
            <footer className="mt-10 border-t border-border pt-4">
              <p className="font-body text-xs leading-relaxed text-muted-foreground">
                Kursvideos werden mit Ihrer Zustimmung direkt von Vimeo geladen.{" "}
                <button
                  type="button"
                  onClick={() => revokeConsentMutation.mutate()}
                  disabled={revokeConsentMutation.isPending}
                  className="font-body underline underline-offset-4 hover:text-primary disabled:opacity-60"
                >
                  Video-Zustimmung widerrufen
                </button>
              </p>
            </footer>
          ) : null}
        </div>
      </div>

    </div>
  );
}
